
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { updateProduct } from '../../redux/actions/productActions';

const ProductEditModal = ({ isOpen, toggleModal, product, onSave }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    size: [],
    color: [],
    colorName: [],
    stock: '',
    discountPrice: '',
    tags: '',
    isActive: true,
  });
  // const [errors, setErrors] = useState({});

  // Initialize form data when product changes
  useEffect(() => {
    if (product) {
      const colorData = product.color.map((color, index) => ({
        value: color,
        name: product.colorName[index] || '',
      }));

      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        size: product.size || [],
        color: colorData,
        stock: product.stock || '',
        discountPrice: product.discountPrice || '',
        tags: product.tags || '',
        isActive: product.isActive || false,
      });
    }
  }, [product]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'size') {
      setFormData((prev) => ({
        ...prev,
        size: Array.from(e.target.selectedOptions, (option) => option.value),
      }));
    } else if (name === 'isActive') {
      setFormData((prev) => ({
        ...prev,
        isActive: value === 'true',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle color changes
  const handleColorChange = (index, newColor, newColorName) => {
    const newColors = [...formData.color];
    newColors[index] = { value: newColor, name: newColorName };
    setFormData((prev) => ({
      ...prev,
      color: newColors,
    }));
  };

  // Add a new color
  const handleAddColor = () => {
    setFormData((prev) => ({
      ...prev,
      color: [...prev.color, { value: '#000000', name: '' }],
    }));
  };

  // Remove a color
  const handleRemoveColor = (index) => {
    const newColors = formData.color.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      color: newColors,
    }));
  };

  // Save changes
  const handleSave = async () => {
    try {
      const colors = formData.color.map((c) => c.value);
      const colorNames = formData.color.map((c) => c.name);

      const updatedProductData = {
        ...formData,
        color: colors,
        colorName: colorNames,
      };

      const updatedProduct = await dispatch(updateProduct(product._id, updatedProductData));
      onSave(updatedProduct);
      toggleModal();
      toast.success('Product updated successfully!');
    } catch (error) {
      toast.error('Failed to update product!');
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 ${isOpen ? 'flex' : 'hidden'} items-center justify-center bg-black bg-opacity-50 p-4`}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Modal Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Edit Product</h2>
            <button
              onClick={toggleModal}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form className="space-y-4">
            {/* Name and Price */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Product Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Price</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Sizes */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Available Sizes</label>
              <div className="flex flex-wrap gap-3">
                {['S', 'M', 'L', 'XL', 'XXL','FREE'].map((size) => (
                  <label key={size} className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="size"
                      value={size}
                      checked={formData.size.includes(size)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, size: [...formData.size, size] });
                        } else {
                          setFormData({ ...formData, size: formData.size.filter((s) => s !== size) });
                        }
                      }}
                      className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">{size}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Colors */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Colors</label>
              <div className="space-y-3">
                {formData.color.map((color, index) => (
                  <div key={index} className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                    <input
                      type="color"
                      value={color.value}
                      onChange={(e) => handleColorChange(index, e.target.value, color.name)}
                      className="w-12 h-12 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      placeholder="Color name"
                      value={color.name}
                      onChange={(e) => handleColorChange(index, color.value, e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveColor(index)}
                      className="px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddColor}
                  className="mt-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Add Color
                </button>
              </div>
            </div>

            {/* Stock, Discount, Tags, and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Stock</label>
                <input
                  type="number"
                  name="stock"
                  value={formData.stock}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Discount Price</label>
                <input
                  type="number"
                  name="discountPrice"
                  value={formData.discountPrice}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Tags (comma-separated)</label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Status</label>
                <select
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleSave}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex-1"
              >
                Save Changes
              </button>
              <button
                type="button"
                onClick={toggleModal}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors flex-1"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductEditModal;